import { PrismicRichText } from '@prismicio/react';

import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { DropdownSelect } from '@components/atoms/DropdownSelect';
import { PostsTile } from '@components/molecules/PostsTile';
import { SliceIconBanner } from '@components/slices/SliceIconBanner';

import { linkResolver } from '@prismic/prismic-config';
import { Tag } from '@prismic/gqlTypes/prismicTypes';

import {
  BlogHomeIconBannerFragment,
  BlogPostFragment,
} from '@prismic//gqlTypes/prismicOperations';

import BlogIcon from '../../images/blog.svg';
import bgImagePath from '../../images/tags-list-bg.png';
import { commonMessages } from '../../intl';
import { BlogPostsSlider } from './BlogPostsSlider';
import { ISortOption, sortOptions } from './sortOptions';

import * as S from './styles';

interface ITagsList {
  tagName: string;
  isActive: boolean;
}

interface IProps {
  canLoadMore: boolean;
  posts: BlogPostFragment[];
  featuresPosts: BlogPostFragment[];
  tagsList: ITagsList[];
  sortBy: ISortOption | null;
  title: RichText;
  iconBannerSlice: BlogHomeIconBannerFragment;
  loadMorePosts: () => void;
  setPostsSorting: (value: ISortOption) => void;
  toggleTag: (tagName: string) => void;
  clearSelectedTags: () => void;
}

const Page: React.FC<IProps> = ({
  canLoadMore,
  posts,
  tagsList,
  loadMorePosts,
  setPostsSorting,
  sortBy,
  featuresPosts,
  iconBannerSlice,
  toggleTag,
  clearSelectedTags,
  title,
}) => {
  const isAllTopicsShowed = useMemo(() => {
    if (tagsList) {
      return tagsList.every((tag) => tag.isActive === false);
    }
    return true;
  }, [tagsList]);

  const intl = useIntl();

  return (
    <S.Wrapper className="blog-index">
      <S.Content>
        <S.Icon src={BlogIcon} />
        {title && (
          <S.Title>
            <PrismicRichText field={title} />
          </S.Title>
        )}

        {/* Fetured slider */}
        <BlogPostsSlider featuresPosts={featuresPosts} />

        {iconBannerSlice && (
          <S.SliceWrapper>
            <SliceIconBanner slice={iconBannerSlice} />
          </S.SliceWrapper>
        )}

        {/* tags list */}
        <S.Filters>
          <img src={bgImagePath.src} alt="" />
          <S.TagsWrapper>
            <S.TagsList>
              <li>
                <button
                  className={isAllTopicsShowed ? 'active' : ''}
                  onClick={clearSelectedTags}
                  type="button"
                >
                  <span>{intl.formatMessage(commonMessages.allTopics)}</span>
                </button>
              </li>
              {tagsList?.map((tag, i) => (
                <li className="" key={`tag_${i}`}>
                  <button
                    className={tag.isActive ? 'active' : ''}
                    onClick={() => toggleTag(tag.tagName)}
                    type="button"
                  >
                    <span>{tag.tagName}</span>
                  </button>
                </li>
              ))}
            </S.TagsList>
          </S.TagsWrapper>
          <DropdownSelect
            options={[
              sortOptions.firstPublicationDateDESC,
              sortOptions.firstPublicationDateASC,
            ]}
            onChange={setPostsSorting}
            value={sortBy}
          />
        </S.Filters>

        {/* posts cards */}
        <S.List>
          {posts?.map((item) => (
            <PostsTile
              key={item._meta.id}
              link={linkResolver(item)}
              image={item.image}
              title={item.title}
              tags={item?.tags.map(({ tag }) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const _tag = tag as Partial<Tag>;
                return _tag.name;
              })}
            />
          ))}
        </S.List>
        <S.BorderDiv />
        {canLoadMore && (
          <S.ShowMoreButtonWrapper>
            <S.ShowMoreButton className="showmore" onClick={loadMorePosts}>
              {intl.formatMessage(commonMessages.showMore)}
            </S.ShowMoreButton>
          </S.ShowMoreButtonWrapper>
        )}
      </S.Content>
    </S.Wrapper>
  );
};

export default Page;
